import React, { useState, useEffect } from "react";
import ColumnChooser from "../task-list/components/column-chooser";
import Modal from "../modal";
import PaginatedTable from "../table";
import TaskNav from "../task-nav/task-nav";

import { COLUMNS, DEFAULT_FILTER, DEFAULT_SORT, DEFAULT_COLUMNS } from "./lib/constants";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons";
import { Textarea } from "components/form-inputs/inputs";
import { filterTask, getTaskStatusTypes } from "components/task-list/lib/taskData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getStorageValue, setStorageValue } from "shared/storage";
import { useAvailableUserAssignments } from "shared/hooks/api/use-available-user-assignments";
import { useGetAssignmentOptions } from "shared/hooks/api/use-get-assignment-options";
import { useAlert } from "hooks/useAlert";
import { useAssignments } from "shared/hooks/use-assignments";
import { useNavigate } from "react-router-dom";
import { useSettings } from "shared/hooks/use-settings";
import { isEmpty } from "lodash";

const TASKLIST_FILTER = "tasklist-filter-scheduling";
const TASKLIST_SORT = "tasklist-sort-scheduling";
const type = "profiling";
const ASSIGNED_GROUP_KEY = "profilingTasksAssignmentGroupKey";
const ASSIGNED_USER_KEY = "userId";

const ProfilingListIndex = () => {
    const navigate = useNavigate();
    const { toggleAlert } = useAlert();
    const { settings } = useSettings();
    const storedFilter = getStorageValue(TASKLIST_FILTER);
    const storedSort = getStorageValue(TASKLIST_SORT);
    const defaultFilter = storedFilter && Array.isArray(storedFilter) ? storedFilter : DEFAULT_FILTER;
    const defaultSort = storedSort && Array.isArray(storedSort) ? storedSort : DEFAULT_SORT;
    const [appendSettings, setAppendSettings] = useState({
        page: 1,
        sorted: defaultSort,
    });

    const [initialAssignmentParams, setInitialAssignmentParams] = useState({
        availableUsersAssignments: [],
        availableAssignments: [],
        users: [],
    });

    const [teamFilter, setTeamFilter] = useState("");
    const [pulse, setPulse] = useState(false);
    const [upcs, setUpcs] = useState("");

    const { userAssignmentOptions, assignmentOptions, updateTeamAssignments, updateUserAssignments, populateUsers, populateUserAssignments } =
        useAssignments(type, initialAssignmentParams);
    const { data: assignments, isSuccess: assignmentsLoaded } = useGetAssignmentOptions(type);
    const { data: availableUserAssignments, isSuccess } = useAvailableUserAssignments();

    const [showModalColumns, setShowModalColumns] = useState(false);
    const [state, setState] = useState({
        columns: [],
        statusTypes: [],
    });

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert({ group: "Profiling" });
        }
    }, [settings.globalNotifications.messages.length, toggleAlert]);

    useEffect(() => {
        if (assignmentsLoaded && isSuccess) {
            setInitialAssignmentParams({
                availableAssignments: assignments,
                availableUsersAssignments: availableUserAssignments,
                users: populateUsers(availableUserAssignments),
            });
        }
    }, [assignments, assignmentsLoaded, availableUserAssignments, isSuccess, populateUsers]);

    useEffect(() => {
        updateTeamAssignments(teamFilter);
        //TODO: check why adding updateTeamAssignment is causing re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamFilter]);

    useEffect(() => {
        setPulse((p) => !p);
    }, [upcs]);

    useEffect(() => {
        if (!isEmpty(initialAssignmentParams.availableAssignments)) {
            getTaskStatusTypes("scheduling.default").then((taskStatus) => {
                const userColumns = getUserColumns();
                const filter = getDefaultFilters();

                setStorageValue(TASKLIST_FILTER, filter);

                setState({
                    statusTypes: taskStatus,
                    columns: userColumns,
                });
            });
        }
    }, [initialAssignmentParams]);

    const getDefaultFilters = () => {
        const newDefaultFilter = defaultFilter || [];
        const storedTeam = newDefaultFilter.find((team) => team.id === ASSIGNED_GROUP_KEY);
        const storedUser = newDefaultFilter.find((assignedUser) => assignedUser.id === ASSIGNED_USER_KEY);
        const user = getStorageValue("user");
        const loggedUser = initialAssignmentParams.users.find(({ email }) => user.email === email);

        if (!storedTeam && loggedUser) {
            const relatedTeams = populateUserAssignments(loggedUser.value.toString(), initialAssignmentParams.availableAssignments);
            setTeamFilter(relatedTeams[0].value);

            if (relatedTeams.length > 0) {
                newDefaultFilter.push({ id: ASSIGNED_GROUP_KEY, value: relatedTeams[0].value });
            }
        }

        if (!storedUser && loggedUser) {
            newDefaultFilter.push({ id: ASSIGNED_USER_KEY, value: loggedUser.value.toString() });
            updateUserAssignments(loggedUser.value.toString());
        }

        setAppendSettings({
            ...appendSettings,
            filtered: newDefaultFilter,
        });

        return newDefaultFilter;
    };

    const filterData = async (pageNumber, pageSize, sorted, filter) => {
        //remove duplicates
        filter = filter.filter((el) => el.id !== "upc");

        //add upcs if available
        if (upcs !== "") {
            const upcList = upcs
                .split(/[,\r\n]+/)
                .map((upc) => upc.trim())
                .filter((upc) => upc !== "");

            filter.push({ id: "upc", values: upcList });
        }

        return filterTask(pageNumber, pageSize, sorted, filter);
    };

    const resetDefault = () => {
        setStorageValue(TASKLIST_SORT, null);
        setStorageValue(TASKLIST_FILTER, null);
        resetColumns();
        window.location.reload();
    };

    const rowClick = (e, { original }) => {
        navigate(`/profiling/${original.key}`);
    };

    const openColumnChooser = () => {
        setShowModalColumns(true);
    };

    const closeModal = () => {
        setShowModalColumns(false);
    };

    const getVisibleColumnsKeys = (columns) =>
        Object.keys(state.columns)
            .reduce((arr, key) => {
                if (columns[key].value) {
                    arr.push(key);
                }

                return arr;
            }, [])
            .join(",");

    const getUserColumns = () => {
        const storedColumns = localStorage.getItem("task-list-columns-profiling");
        const storedColumnKeys = storedColumns || [];

        if (storedColumns) {
            return Object.keys(DEFAULT_COLUMNS).reduce((columns, columnKey) => {
                const storedValue = storedColumnKeys.includes(columnKey);
                columns[columnKey] = { ...DEFAULT_COLUMNS[columnKey], value: storedValue };

                return columns;
            }, {});
        }

        return { ...DEFAULT_COLUMNS };
    };

    const updateUserColumns = (columns) => {
        const selectedColumns = getVisibleColumnsKeys(columns);
        setStorageValue("task-list-columns-profiling", selectedColumns);
        setState({ ...state, columns });
        closeModal();
    };

    const resetColumns = () => {
        localStorage.removeItem("task-list-columns-profiling");
        setState({ ...state, columns: DEFAULT_COLUMNS });
        closeModal();
    };

    return (
        <div className="container">
            <TaskNav>
                <div className="button-group">
                    <h1>Profiling Task List</h1>
                </div>
            </TaskNav>
            <div className="button-group -space-between -flex-end">
                <div>
                    <h3>Filter By UPC(s)</h3>
                    <div className="upc-filter">
                        <Textarea
                            name="upc"
                            value={upcs}
                            updateOnChange={(id, value) => {
                                setUpcs(value);
                            }}
                        />
                    </div>
                </div>
                <div className="flex align-items-center justify-content-end">
                    <button
                        className="button -small -right"
                        onClick={resetDefault}
                    >
                        Reset to Default Sort/Filter
                    </button>
                    <button
                        className="button -small -right"
                        onClick={openColumnChooser}
                    >
                        <FontAwesomeIcon icon={faSlidersH} />
                    </button>
                </div>
            </div>
            <Modal
                show={showModalColumns}
                onClose={closeModal}
            >
                <ColumnChooser
                    type="profiling"
                    updateUserColumns={updateUserColumns}
                    resetColumns={resetColumns}
                    userColumns={state.columns}
                />
            </Modal>
            <PaginatedTable
                appendSettings={appendSettings}
                storageSortValue={TASKLIST_SORT}
                storageFilterValue={TASKLIST_FILTER}
                getData={filterData}
                columns={COLUMNS(
                    state.statusTypes,
                    state.columns,
                    settings.locationDataSources,
                    assignmentOptions,
                    userAssignmentOptions,
                    updateUserAssignments,
                    setTeamFilter
                )}
                showPagination={true}
                pollTimeout={15000}
                onTaskRowClick={(s, rowInfo) => {
                    const style = {
                        cursor: "pointer",
                    };

                    if (rowInfo && !rowInfo.original.isCompleted) {
                        style.background = "#f9de473e";
                    }

                    return {
                        onDoubleClick: (e) => rowClick(e, rowInfo),
                        style: style,
                    };
                }}
                updatePulse={pulse}
            />
        </div>
    );
};

export default ProfilingListIndex;
